/**
 * useFeeCalculatorSettings — load + (optionally) save the firm-wide
 * fee calculator configuration.
 *
 * Behaviour:
 * - Loads the singleton row from Supabase on first mount; falls back
 *   to in-app DEFAULTS if the load fails so the calculator stays
 *   usable in dev environments without a configured Supabase.
 * - Exposes `settings` in the camelCase shape the calculator views
 *   expect ({ hourlyRate, minThreshold, feeCap, vatRate }).
 * - `saveSettings(next)` is wired to update the row, stamping
 *   `updated_by` with the authenticated user's email. UPDATE is
 *   gated by Supabase RLS to the adviser; the Tools page never calls
 *   it (only Settings → Fee Calculator does).
 */

import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../auth/AuthProvider';
import { DEFAULTS } from '../components/tools/feeCalculator/feeEngine';
import { getFeeSettings, saveFeeSettings } from '../api/feeCalculatorSettingsApi';

export function useFeeCalculatorSettings() {
  const { user } = useAuth();
  const [settings, setSettings] = useState(DEFAULTS);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    getFeeSettings().then((next) => {
      if (cancelled) return;
      setSettings({ ...DEFAULTS, ...next });
      setLoaded(true);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const saveSettings = useCallback(
    async (next) => {
      const saved = await saveFeeSettings(next, user?.email);
      setSettings({ ...DEFAULTS, ...saved });
      return saved;
    },
    [user?.email]
  );

  return { settings, loaded, saveSettings };
}
